/**
 * screens/Settings/components/PolicyQuad
 * ──────────────────────────────────────────────────────────────────────────
 * The four policy numbers, read-only, in a hairline quad. Also used verbatim by
 * the approver's "Policy in force" screen, which is why it is a component and
 * not markup inside Settings.
 *
 * Read-only is the point. These four values seed approval rules when saved, so
 * editing them in place would silently rewrite the rule list behind the reader's
 * back. Changing them goes through the Approval policy modal, which says so.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { HairlineGrid } from '../../../components';
import { neutral } from '../../../theme/colors';
import { mono, numeric, sans, size as fontSize } from '../../../theme/typography';
import { formatMinor } from '../../../lib/money';

export interface PolicyQuadProps {
  baseCurrency: string | null;
  thresholdMinor: string | null;
  autoUnderMinor: string | null;
  dualOverMinor: string | null;
}

export function PolicyQuad({
  baseCurrency,
  thresholdMinor,
  autoUnderMinor,
  dualOverMinor,
}: PolicyQuadProps) {
  /** An unset figure reads as an em dash, never as zero. Zero is a threshold. */
  const amount = (value: string | null): string =>
    value === null || baseCurrency === null ? '—' : formatMinor(value, baseCurrency);

  const cells = [
    { key: 'currency', label: 'Base currency', value: baseCurrency ?? '—' },
    { key: 'threshold', label: 'Approval over', value: amount(thresholdMinor) },
    { key: 'auto', label: 'Auto-approve under', value: amount(autoUnderMinor) },
    { key: 'dual', label: 'Dual approval over', value: amount(dualOverMinor) },
  ];

  return (
    <HairlineGrid min={160} columns={4}>
      {cells.map((cell) => (
        <View key={cell.key} style={styles.cell}>
          <Text style={styles.label}>{cell.label}</Text>
          <Text style={styles.value}>{cell.value}</Text>
        </View>
      ))}
    </HairlineGrid>
  );
}

const styles = StyleSheet.create({
  cell: { backgroundColor: neutral.surface, paddingVertical: 13, paddingHorizontal: 15 },
  label: {
    ...sans('400'),
    fontSize: fontSize.tableHeader,
    letterSpacing: 0.63,
    textTransform: 'uppercase',
    color: neutral.grey600,
    marginBottom: 6,
  },
  value: { ...mono('400'), fontSize: fontSize.lg, ...numeric, color: neutral.ink },
});

export default PolicyQuad;
